import React from "react";
import { v4 as uuidv4 } from "uuid";

import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";

import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import LoadingOverlay from "@auto/widgets/LoadingOverlay";
import SearchBar from "@auto/widgets/SearchBar";

import { t } from "@auto/services";
import { appContext } from "../AppContext";

import styles from "./ListComponent.module.styl";

const PAGE_SIZE = 50;
const SORT_STORAGE_PREFIX = "list.sort:";
const FACET_STORAGE_PREFIX = "list.facets:";

const normalizeColumns = (columns, gui, actions) => {
  return columns.map((column) => ({
    ...column,
    title: t(column.title),
    ...(column.field === "__actions" && actions
      ? { render: (row) => actions(row, gui) }
      : column.render
        ? { render: (row) => column.render(row, gui) }
        : {})
  }));
};

const safeNumber = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const clampPage = (page, totalCount) => {
  const safePage = Math.max(0, safeNumber(page, 0));
  const lastPage =
    totalCount > 0 ? Math.max(0, Math.ceil(totalCount / PAGE_SIZE) - 1) : 0;

  return Math.min(safePage, lastPage);
};

const renderCellValue = (row, column) => {
  if (column.render) {
    return column.render(row);
  }

  const value = row?.[column.field];

  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

const sortStorageKeyFor = (storageKey) => {
  if (storageKey) {
    return `${SORT_STORAGE_PREFIX}${storageKey}`;
  }

  return `${SORT_STORAGE_PREFIX}${window.location.pathname}`;
};

const facetStorageKeyFor = (storageKey, facets) => {
  const explicit = facets?.storageKey || storageKey;

  if (explicit) {
    return `${FACET_STORAGE_PREFIX}${explicit}`;
  }

  return `${FACET_STORAGE_PREFIX}${window.location.pathname}`;
};

const normalizeStoredSort = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => ({
      field: String(item?.field || "").trim(),
      direction: item?.direction
    }))
    .filter((item) => {
      if (!item.field) {
        return false;
      }

      if (item.direction !== "asc" && item.direction !== "desc") {
        throw new Error(`Invalid stored sort direction: ${item.direction}`);
      }

      return true;
    });
};

const readStoredSort = (storageKey) => {
  const raw = localStorage.getItem(sortStorageKeyFor(storageKey));

  if (!raw) {
    return [];
  }

  const parsed = JSON.parse(raw);

  if (Array.isArray(parsed)) {
    return normalizeStoredSort(parsed);
  }

  if (parsed?.sort && Array.isArray(parsed.sort)) {
    return normalizeStoredSort(parsed.sort);
  }

  if (parsed?.sortField && parsed?.sortDirection) {
    return normalizeStoredSort([
      {
        field: parsed.sortField,
        direction: parsed.sortDirection
      }
    ]);
  }

  return [];
};

const writeStoredSort = (storageKey, sortState) => {
  const key = sortStorageKeyFor(storageKey);
  const normalized = normalizeStoredSort(sortState);

  if (!normalized.length) {
    localStorage.removeItem(key);
    return;
  }

  localStorage.setItem(key, JSON.stringify(normalized));
};

const readStoredFacets = (storageKey, facets) => {
  const defaultValue = facets?.defaultValue || {};

  if (!facets || facets.persist === false) {
    return defaultValue;
  }

  const raw = localStorage.getItem(facetStorageKeyFor(storageKey, facets));

  if (!raw) {
    return defaultValue;
  }

  const parsed = JSON.parse(raw);

  if (facets.normalizeValue) {
    return facets.normalizeValue(parsed);
  }

  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? parsed
    : defaultValue;
};

const writeStoredFacets = (storageKey, facets, value) => {
  if (!facets || facets.persist === false) {
    return;
  }

  const key = facetStorageKeyFor(storageKey, facets);
  const defaultValue = facets.defaultValue || {};
  const isDefault = facets.isDefaultValue
    ? facets.isDefaultValue(value)
    : JSON.stringify(value || {}) === JSON.stringify(defaultValue || {});

  if (isDefault) {
    localStorage.removeItem(key);
    return;
  }

  localStorage.setItem(key, JSON.stringify(value || {}));
};

const nextDirection = (currentDirection) => {
  if (!currentDirection) {
    return "asc";
  }

  if (currentDirection === "asc") {
    return "desc";
  }

  return null;
};

const Navigation = ({
  page,
  totalCount,
  loading,
  onPageChange,
  compact = false
}) => {
  const lastPage =
    totalCount > 0 ? Math.max(0, Math.ceil(totalCount / PAGE_SIZE) - 1) : 0;

  const from = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(totalCount, (page + 1) * PAGE_SIZE);

  return (
    <div className={compact ? styles.navigationCompact : styles.navigation}>
      <span className={styles.pageStatus}>
        {from}-{to} {t("table.of")} {totalCount}
      </span>

      <div className={styles.pageButtons}>
        <Tooltip title={t("table.firstPage")}>
          <span>
            <IconButton
              size="small"
              disabled={loading || page <= 0}
              onClick={() => onPageChange(0)}
            >
              <FirstPageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t("table.previousPage")}>
          <span>
            <IconButton
              size="small"
              disabled={loading || page <= 0}
              onClick={() => onPageChange(page - 1)}
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t("table.nextPage")}>
          <span>
            <IconButton
              size="small"
              disabled={loading || page >= lastPage}
              onClick={() => onPageChange(page + 1)}
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t("table.lastPage")}>
          <span>
            <IconButton
              size="small"
              disabled={loading || page >= lastPage}
              onClick={() => onPageChange(lastPage)}
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

const ListOverlay = ({ open, onClose, children, label = "List overlay" }) => {
  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div className={styles.overlayLayer} role="presentation">
      <button
        type="button"
        className={styles.overlayBackdrop}
        aria-label="Close overlay"
        onClick={onClose}
      />

      <aside
        className={styles.overlayDrawer}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        {children}
      </aside>
    </div>
  );
};

const createFacetController = ({
  facets,
  value,
  setValue,
  open,
  setOpen,
  counts,
  countsLoading
}) => {
  const defaultValue = facets?.defaultValue || {};

  const normalize = (nextValue) => {
    if (facets?.normalizeValue) {
      return facets.normalizeValue(nextValue);
    }

    return nextValue && typeof nextValue === "object" && !Array.isArray(nextValue)
      ? nextValue
      : defaultValue;
  };

  const isDefaultValue = (candidate) => {
    if (facets?.isDefaultValue) {
      return facets.isDefaultValue(candidate);
    }

    return JSON.stringify(candidate || {}) === JSON.stringify(defaultValue || {});
  };

  const activeCount = facets?.getActiveCount
    ? facets.getActiveCount(value)
    : isDefaultValue(value)
      ? 0
      : 1;

  return {
    value,
    counts: counts || {},
    countsLoading: countsLoading === true,
    open,
    activeCount,
    setOpen,
    openDrawer: () => setOpen(true),
    closeDrawer: () => setOpen(false),
    toggleDrawer: () => setOpen((current) => !current),
    setValue: (nextValue) => setValue(normalize(nextValue)),
    patchValue: (patch) => {
      setValue((current) => normalize({ ...(current || {}), ...(patch || {}) }));
    },
    reset: () => setValue(defaultValue),
    isDefaultValue
  };
};

// Optional controlled row selection. Without projection.selection, lists render
// exactly as before. All IDs are supplied by the caller, not inferred from rows.
// selection: { selectedKeys, getRowId, isSelectable?, onSelectionChange,
//              disabled?, selectPageLabel?, getRowLabel? }
const ListTable = ({
  columns,
  loadData,
  selection,
  noSearch = false,
  localData = false,
  storageKey,
  facets,
  beforeNavigationControl,
  overlay,
  overlayOpen,
  onOverlayClose,
  activeFiltersControl
}) => {
  const isRtl = appContext.getLanguage() === "ar";

  const initialSort = React.useMemo(
    () => readStoredSort(storageKey),
    [storageKey]
  );

  const initialFacetValue = React.useMemo(
    () => readStoredFacets(storageKey, facets),
    [storageKey, facets]
  );

  const [rows, setRows] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [sortState, setSortState] = React.useState(initialSort);
  const [facetValue, setFacetValue] = React.useState(initialFacetValue);
  const [facetCounts, setFacetCounts] = React.useState({});
  const [facetCountsLoading, setFacetCountsLoading] = React.useState(false);
  const [internalOverlayOpen, setInternalOverlayOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [loadingMessage] = React.useState(undefined);

  const requestIdRef = React.useRef(0);
  const countRequestIdRef = React.useRef(0);

  const visibleColumns = React.useMemo(
    () => columns.filter((column) => column.hidden !== true),
    [columns]
  );

  const selectedKeys = selection ? new Set(selection.selectedKeys || []) : null;
  const selectablePageKeys = selection
    ? rows.filter((row) => selection.isSelectable?.(row) !== false)
      .map((row) => selection.getRowId(row))
    : [];
  const allPageSelected = selectablePageKeys.length > 0 &&
    selectablePageKeys.every((key) => selectedKeys.has(key));
  const somePageSelected = selectablePageKeys.some((key) => selectedKeys.has(key));

  const changeSelection = (keys, select) => {
    const next = new Set(selectedKeys);
    keys.forEach((key) => select ? next.add(key) : next.delete(key));
    selection.onSelectionChange(next);
  };

  const drawerOpen = overlayOpen === undefined ? internalOverlayOpen : overlayOpen;
  const setDrawerOpen = React.useCallback(
    (nextValue) => {
      const resolved = typeof nextValue === "function"
        ? nextValue(drawerOpen)
        : nextValue;

      if (overlayOpen === undefined) {
        setInternalOverlayOpen(resolved);
      }

      if (!resolved) {
        onOverlayClose?.();
      }
    },
    [drawerOpen, overlayOpen, onOverlayClose]
  );

  React.useEffect(() => {
    setSortState(readStoredSort(storageKey));
    setPage(0);
  }, [storageKey]);

  React.useEffect(() => {
    setFacetValue(readStoredFacets(storageKey, facets));
    setFacetCounts({});
    setPage(0);
  }, [storageKey, facets]);

  React.useEffect(() => {
    writeStoredSort(storageKey, sortState);
  }, [storageKey, sortState]);

  React.useEffect(() => {
    writeStoredFacets(storageKey, facets, facetValue);
  }, [storageKey, facets, facetValue]);

  const sort = React.useMemo(() => {
    return sortState.map((item) => {
      const column = visibleColumns.find(
        (candidate) => candidate.field === item.field
      );

      if (!column) {
        throw new Error(`Sort column is not visible: ${item.field}`);
      }

      return {
        field: item.field,
        direction: item.direction,
        column
      };
    });
  }, [sortState, visibleColumns]);

  const fetchFacetCounts = React.useCallback(async () => {
    if (!facets?.loadCounts || localData) {
      return;
    }

    const requestId = ++countRequestIdRef.current;
    setFacetCountsLoading(true);

    try {
      const counts = await facets.loadCounts({
        search,
        filters: facetValue,
        sort
      });

      if (requestId === countRequestIdRef.current) {
        setFacetCounts(counts || {});
      }
    } finally {
      if (requestId === countRequestIdRef.current) {
        setFacetCountsLoading(false);
      }
    }
  }, [facets, localData, search, facetValue, sort]);

  const fetchRows = React.useCallback(async () => {
    if (localData) {
      const localRows = Array.isArray(loadData) ? loadData : [];
      setRows(localRows);
      setTotalCount(localRows.length);
      return;
    }

    const requestId = ++requestIdRef.current;

    setLoading(true);

    try {
      const result = await loadData({
        limit: PAGE_SIZE,
        page,
        pageSize: PAGE_SIZE,
        search,
        sort,
        filters: facetValue
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      const nextRows = Array.isArray(result?.data) ? result.data : [];
      const nextTotalCount = safeNumber(result?.totalCount, nextRows.length);
      const nextPage = clampPage(result?.page ?? page, nextTotalCount);

      setRows(nextRows);
      setTotalCount(nextTotalCount);

      if (nextPage !== page) {
        setPage(nextPage);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    localData,
    loadData,
    page,
    search,
    sort,
    facetValue
  ]);

  React.useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  React.useEffect(() => {
    fetchFacetCounts();
  }, [fetchFacetCounts]);

  const changePage = (nextPage) => {
    setPage(clampPage(nextPage, totalCount));
  };

  const requestSearch = (nextSearch) => {
    setPage(0);
    setSearch(String(nextSearch || "").trim());
  };

  const clearSearch = () => {
    setPage(0);
    setSearch("");
  };

  const setFacetValueAndResetPage = React.useCallback((nextValue) => {
    setPage(0);
    setFacetValue((current) => (
      typeof nextValue === "function" ? nextValue(current) : nextValue
    ));
  }, []);

  const liveFacetController = React.useMemo(
    () => createFacetController({
      facets,
      value: facetValue,
      setValue: setFacetValueAndResetPage,
      open: drawerOpen,
      setOpen: setDrawerOpen,
      counts: facetCounts,
      countsLoading: facetCountsLoading
    }),
    [facets, facetValue, setFacetValueAndResetPage, drawerOpen, setDrawerOpen, facetCounts, facetCountsLoading]
  );

  const toggleSort = (column, event) => {
    if (!column.field || column.sorting === false || column.field === "__actions") {
      return;
    }

    const additive = event?.shiftKey === true;

    setPage(0);

    setSortState((current) => {
      const existing = current.find((item) => item.field === column.field);
      const direction = nextDirection(existing?.direction);

      if (!additive) {
        return direction
          ? [{ field: column.field, direction }]
          : [];
      }

      const withoutColumn = current.filter((item) => item.field !== column.field);

      return direction
        ? [...withoutColumn, { field: column.field, direction }]
        : withoutColumn;
    });
  };

  const renderSortIcon = (column) => {
    const index = sortState.findIndex((item) => item.field === column.field);

    if (index < 0) {
      return null;
    }

    const item = sortState[index];

    return (
      <span className={styles.sortState}>
        {item.direction === "asc" ? (
          <ArrowUpwardIcon fontSize="inherit" />
        ) : (
          <ArrowDownwardIcon fontSize="inherit" />
        )}

        {sortState.length > 1 ? (
          <span className={styles.sortPriority}>{index + 1}</span>
        ) : null}
      </span>
    );
  };

  const renderedBeforeNavigation = beforeNavigationControl
    ? beforeNavigationControl({ facetController: liveFacetController })
    : facets?.renderButton
      ? facets.renderButton(liveFacetController)
      : null;

  const renderedOverlay = overlay
    ? overlay({ facetController: liveFacetController })
    : facets?.renderDrawer
      ? facets.renderDrawer(liveFacetController)
      : null;

  const renderedActiveFilters = activeFiltersControl
    ? activeFiltersControl({ facetController: liveFacetController })
    : facets?.renderChips && liveFacetController.activeCount > 0
      ? facets.renderChips(liveFacetController)
      : null;

  const hasRenderedActiveFilters = (() => {
    if (liveFacetController.activeCount <= 0 && !activeFiltersControl) {
      return false;
    }

    if (renderedActiveFilters === null || renderedActiveFilters === undefined || renderedActiveFilters === false) {
      return false;
    }

    if (typeof renderedActiveFilters === "string") {
      return renderedActiveFilters.trim().length > 0;
    }

    return React.Children.count(renderedActiveFilters) > 0;
  })();

  return (
    <LoadingOverlay active={loading} spinner text={loadingMessage}>
      <div className={styles.table} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.stickyToolbar}>
          <div className={styles.toolbarLeft}>
            {renderedBeforeNavigation ? (
              <div className={styles.beforeNavigationControl}>
                {renderedBeforeNavigation}
              </div>
            ) : null}

            <Navigation
              page={page}
              totalCount={totalCount}
              loading={loading}
              onPageChange={changePage}
              compact
            />
          </div>

          {!noSearch && !localData ? (
            <div className={styles.toolbarRight}>
              <SearchBar
                className={styles.searchBar}
                value={search}
                disabled={loading}
                placeholder={t("table.search")}
                onRequestSearch={requestSearch}
                onCancelSearch={clearSearch}
              />
            </div>
          ) : null}
        </div>

        {hasRenderedActiveFilters ? (
          <div className={styles.activeFiltersControl}>
            {renderedActiveFilters}
          </div>
        ) : null}

        <div className={styles.tableScroll}>
          <table className={styles.nativeTable}>
            <thead>
              <tr>
                {selection ? (
                  <th className={styles.selectionHeader}>
                    <Checkbox
                      size="small"
                      checked={allPageSelected}
                      indeterminate={somePageSelected && !allPageSelected}
                      disabled={selection.disabled === true || !selectablePageKeys.length}
                      onChange={() => changeSelection(selectablePageKeys, !allPageSelected)}
                      inputProps={{ "aria-label": selection.selectPageLabel || "Select current page" }}
                    />
                  </th>
                ) : null}
                {visibleColumns.map((column) => {
                  const sortable =
                    column.field &&
                    column.field !== "__actions" &&
                    column.sorting !== false;

                  return (
                    <th
                      key={column.field || column.title}
                      className={sortable ? styles.sortableHeader : ""}
                      onClick={(event) => toggleSort(column, event)}
                      title={sortable ? "Click to sort. Shift-click for multi-sort." : undefined}
                    >
                      <span className={styles.headerCell}>
                        <span>{column.title}</span>
                        <span className={styles.sortIcon}>
                          {renderSortIcon(column)}
                        </span>
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {rows.length ? (
                rows.map((row, rowIndex) => {
                  const rowKey = selection ? selection.getRowId(row) : null;
                  const selectable = selection && selection.isSelectable?.(row) !== false;

                  return (
                    <tr key={selection ? rowKey : (row.id ?? rowIndex)}>
                      {selection ? (
                        <td className={styles.selectionCell}>
                          {selectable ? (
                            <Checkbox
                              size="small"
                              checked={selectedKeys.has(rowKey)}
                              disabled={selection.disabled === true}
                              onChange={() => changeSelection([rowKey], !selectedKeys.has(rowKey))}
                              inputProps={{
                                "aria-label": selection.getRowLabel?.(row) || String(rowKey)
                              }}
                            />
                          ) : null}
                        </td>
                      ) : null}
                      {visibleColumns.map((column) => (
                        <td key={column.field || column.title}>
                          {renderCellValue(row, column)}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={visibleColumns.length + (selection ? 1 : 0) || 1}
                    className={styles.emptyCell}
                  >
                    {t("table.noData")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.bottomBar}>
          <Navigation
            page={page}
            totalCount={totalCount}
            loading={loading}
            onPageChange={changePage}
          />
        </div>

        <ListOverlay
          open={drawerOpen && !!renderedOverlay}
          onClose={liveFacetController.closeDrawer}
          label={facets?.overlayLabel || "List filters"}
        >
          {renderedOverlay}
        </ListOverlay>
      </div>
    </LoadingOverlay>
  );
};

export const ListComponent = ({ gui, projection }) => {
  const {
    columns = [],
    fetch,
    actions,
    selection,
    facets,
    noSearch,
    localData,
    data,
    storageKey,
    beforeNavigationControl,
    overlay,
    overlayOpen,
    onOverlayClose,
    activeFiltersControl
  } = projection;

  if (selection && (
    typeof selection.getRowId !== "function" ||
    typeof selection.onSelectionChange !== "function"
  )) {
    throw new Error("projection.selection requires getRowId and onSelectionChange");
  }

  const tableColumns = React.useMemo(
    () => normalizeColumns(columns, gui, actions),
    [columns, gui, actions]
  );

  const loadData = React.useCallback(
    async (query) => {
      if (localData) {
        return data || [];
      }

      return await fetch(query);
    },
    [fetch, localData, data]
  );

  if (!fetch && !localData) {
    throw new Error("projection.fetch is required for remote lists");
  }

  return (
    <div className={styles.page}>
      {typeof facets === "function" ? (
        <div className={styles.filters}>
          {facets(gui)}
        </div>
      ) : null}

      <ListTable
        columns={tableColumns}
        loadData={loadData}
        selection={selection}
        noSearch={noSearch}
        localData={localData}
        storageKey={storageKey}
        facets={typeof facets === "function" ? null : facets}
        beforeNavigationControl={beforeNavigationControl}
        overlay={overlay}
        overlayOpen={overlayOpen}
        onOverlayClose={onOverlayClose}
        activeFiltersControl={activeFiltersControl}
      />
    </div>
  );
};

export const displayList = (gui, projection) => {
  const uuid = uuidv4();

  return {
    uuid,
    view: () => (
      <ListComponent
        key={uuid}
        gui={gui}
        projection={projection}
      />
    ),
    getContext: projection.getContext
  };
};

export default ListComponent;
